import React, { useEffect, useRef, useState } from 'react';

import { ContainedButton, OutlinedButton } from '../../modules/Buttons';
import Modal from '../../modules/Modal';
import { useAppDispatch } from '../../../store/hooks';
import { deleteFolderByIdFromAllContentPage, getFolderDeleteImpactFromAllContentPage } from '../../../api/foldersApi';
import styles from './DeleteFolderModal.module.scss';
import { getUserOrganization } from '../../../api';
import { saveMyOrganization } from '../../../store/user/userSlice';
import { USER } from '../../../enums';
import { useHotkeys } from 'react-hotkeys-hook';

type TDeleteFolderModal = {
  open: boolean;
  switchModal: () => void;
  folderId: string;
  owner: USER;
  fetchDataCallback?: () => void;
};

const DeleteFolderModal: React.FC<TDeleteFolderModal> = ({ open, switchModal, folderId, owner, fetchDataCallback }) => {
  const [loading, setLoading] = useState(false);
  const [impactLoading, setImpactLoading] = useState(false);
  const [impactedComposers, setImpactedComposers] = useState<string[]>([]);
  const submitButtonRef = useRef<HTMLDivElement>(null);
  const onModalClose = () => {
    switchModal();
  };
  const dispatch = useAppDispatch();

  const handleDelete = async () => {
    setLoading(true);
    try {
      await deleteFolderByIdFromAllContentPage(folderId);
      if (fetchDataCallback) {
        fetchDataCallback();
      }
      getUserOrganization()
        .then((resp) => {
          dispatch(saveMyOrganization(resp.data));
        })
        .catch((error) => console.error(error));
    } catch (err) {
      console.log(err);
      // @ts-ignore
      if (err?.response?.data?.message) {
        // @ts-ignore
        notification.showMessage(err?.response?.data?.message);
      }
    } finally {
      onModalClose();
      setLoading(false);
    }
  };

  useEffect(() => {
    let active = true;
    if (!open || !folderId) return;

    setImpactLoading(true);
    setImpactedComposers([]);

    getFolderDeleteImpactFromAllContentPage(folderId)
      .then((response) => {
        if (!active) return;
        const names = Array.isArray(response?.data) ? response.data : [];
        setImpactedComposers(
          [...new Set(names.map((name: string) => String(name || '').trim()).filter(Boolean))],
        );
      })
      .catch(() => {
        if (active) setImpactedComposers([]);
      })
      .finally(() => {
        if (active) setImpactLoading(false);
      });

    return () => {
      active = false;
    };
  }, [open, folderId]);

  useHotkeys('Enter', () => {
    if (open && !loading) submitButtonRef?.current?.click();
  });

  return (
    <Modal open={open} onClose={onModalClose} loading={loading}>
      <div className={styles.modalBody}>
        <div className={styles.modalTitle}>Are you sure you want to delete {owner?.username || owner?.email}'s folder?</div>
        {!impactLoading && !!impactedComposers.length && (
          <>
            <div className={styles.modalSubtitle}>
              Deleting this folder will also remove clips from these Online Composers. Composers that only depend on this source will be deleted:
            </div>
            <div className={styles.namesList}>
              {impactedComposers.map((name) => (
                <div key={name}>{name}</div>
              ))}
            </div>
          </>
        )}
        <div className={styles.form}>
          <div className={styles.actionBtns}>
            <OutlinedButton onClick={handleDelete} kind="default" disabled={loading || impactLoading}>
              <div ref={submitButtonRef}>Yes, I am sure</div>
            </OutlinedButton>

            <ContainedButton kind="purple" onClick={onModalClose} disabled={loading || impactLoading}>
              No, cancel
            </ContainedButton>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default DeleteFolderModal;
